import {map, Observable, tap} from 'rxjs';
import {notificationsManager} from '@innowise-group/ui-kit';
import {AxiosRequestConfig} from 'axios';
import {
  WorkspacesRequestsResponse,
  RequestsApi,
  WorkspaceRequestActions,
  CreateWorkspaceRequestPayload,
  EditWorkspaceRequestPayload,
  ApproveWorkspaceRequestPayload,
  RawFiltersParams,
  RequestsProcessedResponse,
  RequestsResponse,
} from './requests-api.types';
import {Http, inject, injectable, GlobalContainerTypes, Employee, Request, RequestStatus} from '../..';
import {RequestsNormalization} from '../requests-normalization';

@injectable()
class RequestsApiService implements RequestsApi {
  public static readonly type = GlobalContainerTypes.RequestsApi;

  @inject(GlobalContainerTypes.Http) private http: Http;
  @inject(GlobalContainerTypes.RequestsNormalization) private requestsNormalization: RequestsNormalization;

  public getWorkspacesRequestsList(
    filters: RawFiltersParams,
    config?: AxiosRequestConfig,
  ): Observable<RequestsProcessedResponse> {
    const params = this.createUrlSearchParams(filters);
    return this.http.GET<RequestsResponse>(`/workspaces_requests/?${params}`, config).pipe(
      map(({count, results}) => ({
        count,
        requests: results.map(this.requestsNormalization.normalizeWorkspaceRequestFromApi),
      })),
    );
  }

  public getRequestsListWorkspacesCount(): Observable<number> {
    return this.http.GET<{count: number}>(`/workspaces_requests/requests-count/`).pipe(map(({count}) => count));
  }

  public getWorkspaceRequestById(requestId: string): Observable<Request> {
    return this.http
      .GET<WorkspacesRequestsResponse>(`/workspaces_requests/${requestId}/`)
      .pipe(map(this.requestsNormalization.normalizeWorkspaceRequestFromApi));
  }

  public workspaceApproveRequest(
    requestId: string,
    payload: ApproveWorkspaceRequestPayload,
  ): Observable<{status: RequestStatus; officeManager: Employee}> {
    return this.http
      .PATCH<WorkspaceRequestActions, ApproveWorkspaceRequestPayload>(
        `/workspaces_requests/${requestId}/approve-request/`,
        payload,
      )
      .pipe(
        map(this.requestsNormalization.normalizeWorkspaceActionRequestFromApi),
        tap(() => {
          notificationsManager.success({
            title: 'success',
            subtitle: 'acceptedRequest',
            subtitleProps: {requestNumber: requestId},
          });
        }),
      );
  }

  public workspaceRejectRequest(
    requestId: string,
    comment: string,
  ): Observable<{status: RequestStatus; officeManager: Employee}> {
    return this.http
      .PATCH<WorkspaceRequestActions>(`/workspaces_requests/${requestId}/reject-request/`, {manager_comment: comment})
      .pipe(
        map(this.requestsNormalization.normalizeWorkspaceActionRequestFromApi),
        tap(() => {
          notificationsManager.success({
            title: 'success',
            subtitle: 'rejectedRequest',
            subtitleProps: {requestNumber: requestId},
          });
        }),
      );
  }

  public createWorkspaceRequest(request: CreateWorkspaceRequestPayload): Observable<unknown> {
    return this.http.POST(`/workspaces_requests/`, {...request}).pipe(
      tap<void>(() => {
        notificationsManager.success({title: 'success', subtitle: 'bookWorkspace'});
      }),
    );
  }

  public editWorkspaceRequest(requestId: string, request: EditWorkspaceRequestPayload): Observable<unknown> {
    return this.http.PATCH(`/workspaces_requests/${requestId}/`, {...request}).pipe(
      tap<void>(() => {
        notificationsManager.success({title: 'success', subtitle: 'updateWorkspaceRequest'});
      }),
    );
  }

  public deleteWorkspaceRequest(requestId: string): Observable<unknown> {
    return this.http.DELETE(`/workspaces_requests/${requestId}/`).pipe(
      tap<void>(() => {
        notificationsManager.success({title: 'success', subtitle: 'deleteWorkspaceRequest'});
      }),
    );
  }

  public workspaceCanceledRequest(requestId: string): Observable<{status: RequestStatus; officeManager: Employee}> {
    return this.http.PATCH<WorkspaceRequestActions>(`/workspaces_requests/${requestId}/delay-request/`).pipe(
      map(this.requestsNormalization.normalizeWorkspaceActionRequestFromApi),
      tap(() => {
        notificationsManager.success({title: 'success', subtitle: 'canceledWorkspaceRequest'});
      }),
    );
  }

  private createUrlSearchParams(filters: RawFiltersParams): string {
    const params = new URLSearchParams();
    const normalizedFilters = this.requestsNormalization.normalizeRequestsRawFilters(filters);

    Object.keys(normalizedFilters).forEach((key) => {
      if (!normalizedFilters[key]) return;
      if (Array.isArray(normalizedFilters[key])) {
        normalizedFilters[key].forEach((value: string) => {
          params.append(key, value);
        });
      } else {
        params.set(key, normalizedFilters[key]);
      }
    });
    return params.toString();
  }
}

export default RequestsApiService;
